import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { useInvoicesGraphQl } from '@hooks/graphql/useInvoicesGraphQl';
import { VendorInvoice } from '@custom-types/invoices';
import { useSelectedPeriod } from '@context/SelectedPeriodContext';

export type SortField = 'vendor_name' | 'total_adjustments' | 'invoice_status';
export type SortDirection = 'asc' | 'desc';

const useInvoicesDashboardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedMonth: ctxMonth, selectedYear: ctxYear, setPeriod } = useSelectedPeriod();
  const today = dayjs();
  const lastDayOfMonth = today.endOf('month').date();

  // cutoff = 30 OR last day (whichever is smaller)
  const cutoffDay = Math.min(30, lastDayOfMonth);

  // decide effective date
  const effectiveDate = today.date() < cutoffDay ? today.subtract(1, 'month') : today;

  // URL param takes highest precedence, then context, then effective date
  const urlMonth = searchParams.get('month');
  const urlDate = useMemo(() => {
    if (urlMonth && /^\d{4}-\d{2}$/.test(urlMonth)) {
      const parsed = dayjs(urlMonth, 'YYYY-MM');
      if (parsed.isValid()) {
        return parsed;
      }
    }
    return null;
  }, [urlMonth]);

  const ctxDate = (ctxMonth !== null && ctxYear !== null)
    ? dayjs(new Date(ctxYear, ctxMonth, 1))
    : null;

  const startDate = urlDate ?? ctxDate ?? effectiveDate;

  const [selectedMonth, setSelectedMonth] = useState(startDate.month());
  const [selectedYear, setSelectedYear] = useState(startDate.year());

  // Build YYYY-MM string
  const selectedPeriod = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
  const lastSelectablePeriod = `${effectiveDate.year()}-${String(
    effectiveDate.month() + 1
  ).padStart(2, '0')}`;
  const isNextDisabled = selectedPeriod >= lastSelectablePeriod;
  const monthYearLabel = dayjs(`${selectedYear}-${selectedMonth + 1}`).format('MMMM YYYY');

  const pickerValue = dayjs(new Date(selectedYear, selectedMonth, 1));

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // --- Apollo Hook ---
  const { data, loading, saving, saveFeedback, setSaveFeedback, handleEdit, handleSave, handleCreateVendorInvoices, handleCreateNspInvoices, isVendorApprovalPersisted, hasRealChanges, username } = useInvoicesGraphQl({
    monthYear: selectedPeriod,
  });

  const COMBINED_STATUS_RANK: Record<string, number> = {
    in_progress: 0,
    not_approved: 1,
    ready_for_approval: 2,
    approved: 3,
    sent: 4,
  };

  const getCombinedStatusRank = (vendor: VendorInvoice): number => {
    const { invoice_status, approval_status, invoice_approved } = vendor;
    if (invoice_status === 'sent') return COMBINED_STATUS_RANK.sent;
    if (approval_status === 'Approved' || invoice_approved) return COMBINED_STATUS_RANK.approved;
    if (approval_status === 'Not Approved') return COMBINED_STATUS_RANK.not_approved;
    if (approval_status === 'Ready') return COMBINED_STATUS_RANK.ready_for_approval;
    return COMBINED_STATUS_RANK.in_progress;
  };

  // The backend already returns each Counterparty (CIB) invoice immediately after its Primary
  // vendor. Group rows that way before sorting, so sorting only ever reorders Primary vendors —
  // never separates a pair.
  const groupByPrimary = (rows: VendorInvoice[]): { primary: VendorInvoice; counterparties: VendorInvoice[] }[] => {
    const groups: { primary: VendorInvoice; counterparties: VendorInvoice[] }[] = [];
    rows.forEach((row) => {
      if (!row.is_counterparty_invoice) {
        groups.push({ primary: row, counterparties: [] });
      } else if (groups.length > 0) {
        groups[groups.length - 1].counterparties.push(row);
      }
    });
    return groups;
  };

  const sortedData = useMemo<VendorInvoice[]>(() => {
    if (!data) return [];
    const groups = groupByPrimary(data);
    if (sortField) {
      groups.sort((groupA, groupB) => {
        let aVal: string | number;
        let bVal: string | number;
        if (sortField === 'vendor_name') {
          aVal = groupA.primary.vendor_name ?? '';
          bVal = groupB.primary.vendor_name ?? '';
        } else if (sortField === 'total_adjustments') {
          aVal = groupA.primary.total_adjustments ?? 0;
          bVal = groupB.primary.total_adjustments ?? 0;
        } else {
          aVal = getCombinedStatusRank(groupA.primary);
          bVal = getCombinedStatusRank(groupB.primary);
        }
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return groups.flatMap((group) => [group.primary, ...group.counterparties]);
  }, [data, sortField, sortDirection]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncUrl = useCallback((month: number, year: number) => {
    const period = `${year}-${String(month + 1).padStart(2, '0')}`;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('month', period);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // --- Month navigation ---
  const navigateMonth = (dir: number) => {
    let month = selectedMonth + dir;
    let year = selectedYear;
    if (month > 11) {
      month = 0;
      year++;
    } else if (month < 0) {
      month = 11;
      year--;
    }
    setSelectedMonth(month);
    setSelectedYear(year);
    setPeriod(month, year);
    syncUrl(month, year);
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setSelectedMonth(date.month());
      setSelectedYear(date.year());
      setPeriod(date.month(), date.year());
      syncUrl(date.month(), date.year());
    }
  };

  return {
    selectedMonth,
    selectedYear,
    selectedPeriod,
    monthYearLabel,
    isNextDisabled,
    pickerValue,
    navigateMonth,
    handleDateChange,
    data,
    sortedData,
    sortField,
    sortDirection,
    handleSort,
    loading,
    saving,
    saveFeedback,
    setSaveFeedback,
    handleEdit,
    handleSave,
    handleCreateVendorInvoices,
    handleCreateNspInvoices,
    isVendorApprovalPersisted,
    hasRealChanges,
    username,
  };
};

export default useInvoicesDashboardPage;
